import { create } from "zustand";

const useAppStore = create((set, get) => ({
  tables: {},
  appState: {
    breakfast_started: false,
    selected_tables: [],
    active_category_filters: [],
  },

  /**
   * Phase H (Δ-13) — client-only filter state. See C-22.
   *
   *   - selectedMode: null | 'Not Arrived' | 'Waiting' | 'Seated'
   *   - searchQuery: lowercased-on-use, raw user input string
   *
   * NOT persisted across reloads. NOT round-tripped to backend.
   */
  selectedMode: null,
  searchQuery: "",
  setSelectedMode: (mode) => set({ selectedMode: mode }),
  setSearchQuery: (q) => set({ searchQuery: q ?? "" }),

  /**
   * Phase G (Δ-11) — global 1s tick.
   *
   * Bumped by `useTimerTick` while the document is visible. Cards
   * subscribe to this slice via `useAppStore((s) => s.tick)` to force
   * a re-render of their derived elapsed/waiting strings without
   * needing a per-card setInterval.
   *
   * See C-16 (at-most-once-per-second, no catch-up) and C-17 (cards
   * subscribe only to their own slice + `tick`).
   */
  tick: 0,
  bumpTick: () => set((s) => ({ tick: s.tick + 1 })),

  setTables: (tablesList) => {
    const map = {};
    for (const t of tablesList) {
      map[t.code] = t;
    }
    set({ tables: map });
  },

  setAppState: (state) => set({ appState: state }),

  /**
   * CR-0011 Slice 6 — atomic optimistic bulk category update.
   *
   * Mutates the local `tables` map in place. Returns a snapshot of the
   * previous category arrays so the caller can rollback on API error.
   */
  applyBulkCategories: (codes, category, add) => {
    const snapshot = {};
    const tables = { ...get().tables };
    for (const code of codes) {
      const t = tables[code];
      if (!t) continue;
      snapshot[code] = [...(t.categories || [])];
      const set_ = new Set(snapshot[code]);
      if (add) set_.add(category);
      else set_.delete(category);
      tables[code] = { ...t, categories: Array.from(set_) };
    }
    set({ tables });
    return snapshot;
  },

  /**
   * CR-0011 Slice 6 — rollback companion to applyBulkCategories.
   */
  restoreCategoriesSnapshot: (snapshot) => {
    const tables = { ...get().tables };
    for (const code of Object.keys(snapshot)) {
      const t = tables[code];
      if (!t) continue;
      tables[code] = { ...t, categories: snapshot[code] };
    }
    set({ tables });
  },
}));

export default useAppStore;
